import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { FiSave, FiPlus, FiEdit2, FiTrash2, FiAlertCircle, FiCheckCircle, FiEye, FiX } from 'react-icons/fi';
import styles from './AdminAnnouncement.module.css';

const AdminAnnouncement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [globalSpeed, setGlobalSpeed] = useState(5);
  const [formData, setFormData] = useState({
    text: '',
    is_active: true,
    speed: 5,
    bg_color: '#1F5B3A',
    text_color: '#FFFFFF',
    icon: '📢'
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setAnnouncements(data);
      // Set global speed from the first announcement
      if (data.length > 0) setGlobalSpeed(data[0]?.speed || 5);
    }
    setLoading(false);
  };

  const handleGlobalSpeedChange = async (newSpeed) => {
    if (announcements.length === 0) return;
    setGlobalSpeed(newSpeed);
    // Update the first announcement's speed
    const first = announcements[0];
    if (first) {
      const { error } = await supabase
        .from('announcements')
        .update({ speed: newSpeed, updated_at: new Date().toISOString() })
        .eq('id', first.id);
      if (!error) {
        setMessage({ type: 'success', text: 'Global rotation speed updated!' });
        fetchAnnouncements();
      } else {
        setMessage({ type: 'error', text: 'Failed to update speed: ' + error.message });
      }
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const resetForm = () => {
    setFormData({
      text: '',
      is_active: true,
      speed: 5,
      bg_color: '#1F5B3A',
      text_color: '#FFFFFF',
      icon: '📢'
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (announcement) => {
    setFormData({
      text: announcement.text || '',
      is_active: announcement.is_active !== undefined ? announcement.is_active : true,
      speed: announcement.speed || 5,
      bg_color: announcement.bg_color || '#1F5B3A',
      text_color: announcement.text_color || '#FFFFFF',
      icon: announcement.icon || '📢'
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    const { error } = await supabase.from('announcements').delete().eq('id', id);
    if (error) {
      setMessage({ type: 'error', text: 'Failed to delete: ' + error.message });
    } else {
      setMessage({ type: 'success', text: 'Announcement deleted!' });
      fetchAnnouncements();
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.text.trim()) {
      setMessage({ type: 'error', text: 'Please enter announcement text.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    const payload = {
      text: formData.text.trim(),
      is_active: formData.is_active,
      speed: parseInt(formData.speed) || 5,
      bg_color: formData.bg_color,
      text_color: formData.text_color,
      icon: formData.icon || '📢',
      updated_at: new Date().toISOString()
    };

    let error;
    if (editingId) {
      const { error: updateError } = await supabase
        .from('announcements')
        .update(payload)
        .eq('id', editingId);
      error = updateError;
    } else {
      const { error: insertError } = await supabase
        .from('announcements')
        .insert(payload);
      error = insertError;
    }

    if (error) {
      setMessage({ type: 'error', text: 'Failed to save: ' + error.message });
    } else {
      setMessage({ type: 'success', text: `Announcement ${editingId ? 'updated' : 'created'}!` });
      fetchAnnouncements();
      resetForm();
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.adminAnnouncement}>
      <div className={styles.header}>
        <h2>📢 Announcements</h2>
        <p>Manage rotating announcements – all will stay visible for the same duration</p>
      </div>

      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
          {message.text}
        </div>
      )}

      {/* Global Speed Setting */}
      <div className={styles.globalSpeedCard}>
        <div className={styles.globalSpeedContent}>
          <label>🔄 Global Rotation Speed (seconds)</label>
          <div className={styles.globalSpeedInput}>
            <input
              type="number"
              min="2"
              max="30"
              value={globalSpeed}
              onChange={(e) => setGlobalSpeed(parseInt(e.target.value) || 5)}
              className={styles.numberInput}
            />
            <button
              onClick={() => handleGlobalSpeedChange(globalSpeed)}
              className={styles.applySpeedBtn}
            >
              Apply to All
            </button>
          </div>
          <small>Each announcement will display for this many seconds before rotating</small>
        </div>
      </div>

      <div className={styles.toolbar}>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className={styles.addBtn}
        >
          <FiPlus /> Add Announcement
        </button>
      </div>

      {showForm && (
        <div className={styles.formCard}>
          <div className={styles.formHeader}>
            <h3>{editingId ? 'Edit Announcement' : 'New Announcement'}</h3>
            <button onClick={resetForm} className={styles.closeBtn}><FiX /></button>
          </div>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label>Text *</label>
              <textarea
                rows="3"
                name="text"
                value={formData.text}
                onChange={handleFormChange}
                placeholder="Enter announcement message..."
                className={styles.textarea}
                required
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Active</label>
                <div className={styles.toggleWrapper}>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${formData.is_active ? styles.active : ''}`}
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  >
                    {formData.is_active ? '🟢 Active' : '🔴 Inactive'}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Individual Speed (optional)</label>
                <input
                  type="number"
                  name="speed"
                  min="2"
                  max="30"
                  value={formData.speed}
                  onChange={handleFormChange}
                  className={styles.numberInput}
                />
                <small>Individual speed is ignored – global rotation speed applies to all</small>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Background Color</label>
                <div className={styles.colorInputWrapper}>
                  <input
                    type="color"
                    name="bg_color"
                    value={formData.bg_color}
                    onChange={handleFormChange}
                    className={styles.colorPicker}
                  />
                  <input
                    type="text"
                    name="bg_color"
                    value={formData.bg_color}
                    onChange={handleFormChange}
                    className={styles.colorText}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Text Color</label>
                <div className={styles.colorInputWrapper}>
                  <input
                    type="color"
                    name="text_color"
                    value={formData.text_color}
                    onChange={handleFormChange}
                    className={styles.colorPicker}
                  />
                  <input
                    type="text"
                    name="text_color"
                    value={formData.text_color}
                    onChange={handleFormChange}
                    className={styles.colorText}
                  />
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Icon (emoji)</label>
              <input
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleFormChange}
                placeholder="📢"
                maxLength="2"
                className={styles.iconInput}
              />
            </div>

            <div className={styles.formActions}>
              <button type="submit" disabled={saving} className={styles.saveBtn}>
                {saving ? 'Saving...' : <><FiSave /> {editingId ? 'Update' : 'Add'}</>}
              </button>
              <button type="button" onClick={resetForm} className={styles.cancelBtn}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.listCard}>
        <h3>All Announcements ({announcements.length})</h3>
        {announcements.length === 0 ? (
          <p className={styles.emptyState}>No announcements yet. Add your first one!</p>
        ) : (
          <div className={styles.announcementList}>
            {announcements.map((ann) => (
              <div key={ann.id} className={styles.announcementItem}>
                <div className={styles.itemContent}>
                  <div className={styles.itemIcon}>{ann.icon || '📢'}</div>
                  <div className={styles.itemText}>
                    <div className={styles.itemTextMain}>{ann.text}</div>
                    <div className={styles.itemMeta}>
                      <span className={ann.is_active ? styles.statusActive : styles.statusInactive}>
                        {ann.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span>Individual: {ann.speed}s</span>
                      <span style={{ background: ann.bg_color, color: ann.text_color, padding: '0 6px', borderRadius: '4px' }}>
                        Preview
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.itemActions}>
                  <button onClick={() => handleEdit(ann)} className={styles.editBtn}><FiEdit2 /></button>
                  <button onClick={() => handleDelete(ann.id)} className={styles.deleteBtn}><FiTrash2 /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncement;